import { useEffect, useState } from "react";
import { AutoComplete, Alert, Button, Col, Form, Input, InputNumber, Row, Switch } from "antd";
import { t } from "i18next";
import ApiService from "../../core/service/ApiService";

const UNIT_OPTIONS = ['ml', 'adet', 'gram', 'mg', 'lt'].map(value => ({ value }));
const PACKAGE_UNIT_OPTIONS = ['şişe', 'flakon', 'kutu', 'ampul', 'adet', 'paket'].map(value => ({ value }));

/** Sarf tanımı. Stok hareketi olan sarfta kullanım birimi ve ambalaj içeriği kilitlidir. */
export default function ConsumableModal({ consumable, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const locked = !!consumable?.hasMovements;

    useEffect(() => {
        form.setFieldsValue(consumable
            ? {
                name: consumable.name,
                code: consumable.code,
                barcode: consumable.barcode,
                unit: consumable.unit,
                packageUnit: consumable.packageUnit,
                packageSize: consumable.packageSize,
                isDivisible: consumable.isDivisible,
                minStock: consumable.minStock,
                expiryWarningDays: consumable.expiryWarningDays,
                asksUsageMethod: consumable.asksUsageMethod,
                isActive: consumable.isActive,
            }
            : { isDivisible: false, asksUsageMethod: false, isActive: true, packageSize: 1, expiryWarningDays: 30 });
    }, []);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const res = consumable
                ? await ApiService.Consumable.EditConsumable(consumable.id, values)
                : await ApiService.Consumable.CreateConsumable(values);
            if (res.success) onSuccess && onSuccess();
        } finally {
            setLoading(false);
        }
    };

    const unit = Form.useWatch('unit', form);
    const packageUnit = Form.useWatch('packageUnit', form);

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Please enter name') }]}>
                <Input maxLength={255} />
            </Form.Item>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item name="code" label={t('Code')}><Input maxLength={100} /></Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="barcode" label={t('Barcode')}><Input maxLength={100} /></Form.Item>
                </Col>
            </Row>
            {locked && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message={t('This consumable has stock movements; usage unit and package content cannot be changed.')}
                />
            )}
            <Row gutter={12}>
                <Col span={8}>
                    <Form.Item name="unit" label={t('Usage Unit')} rules={[{ required: true, whitespace: true, message: '' }]}>
                        <AutoComplete options={UNIT_OPTIONS} disabled={locked} placeholder="ml" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item name="packageUnit" label={t('Package Unit')} rules={[{ required: true, whitespace: true, message: '' }]}>
                        <AutoComplete options={PACKAGE_UNIT_OPTIONS} placeholder="şişe" />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name="packageSize"
                        label={t('Package Content')}
                        rules={[{ required: true, message: '' }]}
                        extra={packageUnit && unit ? `1 ${packageUnit} = ? ${unit}` : null}
                    >
                        <InputNumber min={0.001} decimalSeparator="," style={{ width: '100%' }} disabled={locked} />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                name="isDivisible"
                label={t('Opened package is divisible')}
                valuePropName="checked"
                extra={t('Off: the opened packages are deducted in full and the remainder is logged as waste. On: only the used amount is deducted (e.g. injector systems).')}
            >
                <Switch />
            </Form.Item>
            <Row gutter={12}>
                <Col span={12}>
                    <Form.Item name="minStock" label={`${t('Minimum Stock')}${unit ? ` (${unit})` : ''}`}>
                        <InputNumber min={0} decimalSeparator="," style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="expiryWarningDays" label={t('Expiry Warning (days)')}>
                        <InputNumber min={0} precision={0} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                name="asksUsageMethod"
                label={t('Ask usage method')}
                valuePropName="checked"
                extra={t('On: manual or automatic injector is selected when the appointment is completed.')}
            >
                <Switch />
            </Form.Item>
            <Form.Item name="isActive" label={t('Active')} valuePropName="checked">
                <Switch />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={loading}>{t('Save')}</Button>
            </Form.Item>
        </Form>
    );
}
